import { useEffect, useState, useCallback } from "react";
import { crmApi } from "@/lib/crm";
import { Card, Stat, Btn, Input, SectionTitle, Empty, Table, Pill, money } from "./kit";

export function PulseTab() {
  const [data, setData] = useState(null);
  useEffect(() => { crmApi("/overview").then(setData).catch(() => {}); }, []);
  if (!data) return <Empty>Loading pulse…</Empty>;
  return (
    <div className="space-y-6" data-testid="crm-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Revenue" value={`CA$${data.revenue_cad.toLocaleString()}`} sub={`${data.orders} orders`} />
        <Stat label="Bags sold" value={data.bags_sold} />
        <Stat label="Customers" value={data.customers} />
        <Stat label="Open tasks" value={data.open_tasks} />
        <Stat label="Recovery queue" value={data.pending_recovery} sub="abandoned 30+ min" />
        <Stat label="Creators pending" value={data.pending_creators} />
        <Stat label="UGC pending" value={data.pending_ugc} />
        <Stat label="SEO to review" value={data.pending_seo} />
      </div>
      <Card>
        <SectionTitle>Recent paid orders</SectionTitle>
        {data.recent_orders.length === 0 ? <Empty>No paid orders yet</Empty> : (
          <Table cols={["Ref", "Email", "Bundle", "Total", "When"]}>
            {data.recent_orders.map((o) => (
              <tr key={o.session_id}>
                <td className="py-2.5 pr-4 font-tech text-xs text-slate-400">{o.session_id.slice(-10)}</td>
                <td className="py-2.5 pr-4">{o.customer_email || "—"}</td>
                <td className="py-2.5 pr-4"><Pill tone="orange">{o.bags || 1} bag{(o.bags || 1) > 1 ? "s" : ""}</Pill></td>
                <td className="py-2.5 pr-4 font-semibold">{money(o.amount)}</td>
                <td className="py-2.5 text-xs text-slate-500">{(o.updated_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

const STAGES = ["lead", "prospect", "customer", "vip", "lost"];

export function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const load = useCallback(() => crmApi("/customers").then(setCustomers).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const setStage = async (id, stage) => { await crmApi(`/customers/${id}`, { method: "PATCH", body: { stage } }); load(); };
  return (
    <Card data-testid="crm-customers">
      <SectionTitle>Customers</SectionTitle>
      {customers.length === 0 ? <Empty>No customers yet — they appear here after a paid order</Empty> : (
        <Table cols={["Email", "Stage", "Orders", "Spent", "Points", "VIP", "Referral code"]}>
          {customers.map((c) => (
            <tr key={c.id}>
              <td className="py-2.5 pr-4">{c.email}</td>
              <td className="py-2.5 pr-4">
                <select value={c.stage} onChange={(e) => setStage(c.id, e.target.value)} data-testid="customer-stage"
                  className="bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-xs outline-none">
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td className="py-2.5 pr-4">{c.orders}</td>
              <td className="py-2.5 pr-4">CA${(c.total_spent || 0).toLocaleString()}</td>
              <td className="py-2.5 pr-4">{c.points}</td>
              <td className="py-2.5 pr-4">{c.vip ? <Pill tone="amber">VIP</Pill> : <Pill>—</Pill>}</td>
              <td className="py-2.5 font-tech text-xs text-[#f97316]">{c.referral_code}</td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

export function OrdersTab() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { crmApi("/orders").then(setOrders).catch(() => {}); }, []);
  return (
    <Card data-testid="crm-orders">
      <SectionTitle>Orders</SectionTitle>
      {orders.length === 0 ? <Empty>No orders yet</Empty> : (
        <Table cols={["Ref", "Email", "Bundle", "Total", "Status", "Referral", "Created"]}>
          {orders.map((o) => (
            <tr key={o.session_id}>
              <td className="py-2.5 pr-4 font-tech text-xs text-slate-400">{o.session_id.slice(-10)}</td>
              <td className="py-2.5 pr-4">{o.customer_email || "—"}</td>
              <td className="py-2.5 pr-4">{o.bags || 1}× 5kg</td>
              <td className="py-2.5 pr-4 font-semibold">{money(o.amount)}</td>
              <td className="py-2.5 pr-4">
                <Pill tone={o.payment_status === "paid" ? "green" : o.payment_status === "pending" ? "amber" : "red"}>
                  {o.payment_status}
                </Pill>
              </td>
              <td className="py-2.5 pr-4 font-tech text-xs text-[#f97316]">{o.referral_code || "—"}</td>
              <td className="py-2.5 text-xs text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

export function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState("");
  const load = useCallback(() => crmApi("/tasks").then(setTasks).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => { e.preventDefault(); if (!title.trim()) return; await crmApi("/tasks", { method: "POST", body: { title } }); setTitle(""); load(); };
  const toggle = async (t) => { await crmApi(`/tasks/${t.id}`, { method: "PATCH", body: { done: !t.done } }); load(); };
  return (
    <Card data-testid="crm-tasks">
      <SectionTitle>Tasks</SectionTitle>
      <form onSubmit={add} className="flex gap-2.5 mb-6">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Follow up with…" data-testid="task-input" />
        <Btn type="submit" data-testid="task-add">Add</Btn>
      </form>
      {tasks.length === 0 ? <Empty>No tasks — add your first follow-up</Empty> : (
        <ul className="space-y-2.5">
          {tasks.map((t) => (
            <li key={t.id} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3">
              <input type="checkbox" checked={t.done} onChange={() => toggle(t)} data-testid="task-toggle"
                className="accent-[#f97316] w-4 h-4" />
              <span className={`text-sm flex-1 ${t.done ? "line-through text-slate-600" : "text-slate-200"}`}>{t.title}</span>
              {t.customer_email && <Pill>{t.customer_email}</Pill>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function RecoveryTab() {
  const [queue, setQueue] = useState([]);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => crmApi("/recovery/queue").then(setQueue).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const send = async (sessionId) => {
    setMsg("");
    try {
      await crmApi("/recovery/send", { method: "POST", body: { session_id: sessionId } });
      setMsg("Recovery email sent");
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Send failed");
    }
  };
  return (
    <Card data-testid="crm-recovery">
      <SectionTitle>Abandoned checkout recovery</SectionTitle>
      <p className="text-xs text-slate-500 mb-5">Checkouts idle 30+ minutes. Emails send only with explicit customer consent — max one automated message per checkout.</p>
      {msg && <p className="text-sm text-[#f97316] mb-4" data-testid="recovery-msg">{msg}</p>}
      {queue.length === 0 ? <Empty>Queue is clear</Empty> : (
        <Table cols={["Email", "Value", "Consent", "Created", ""]}>
          {queue.map((tx) => (
            <tr key={tx.session_id}>
              <td className="py-2.5 pr-4">{tx.customer_email}</td>
              <td className="py-2.5 pr-4 font-semibold">{money(tx.amount)}</td>
              <td className="py-2.5 pr-4">{tx.recovery_consent ? <Pill tone="green">consented</Pill> : <Pill tone="red">no consent</Pill>}</td>
              <td className="py-2.5 pr-4 text-xs text-slate-500">{(tx.created_at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="py-2.5">
                <Btn onClick={() => send(tx.session_id)} disabled={!tx.recovery_consent} data-testid="recovery-send">Send</Btn>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}
